import { ccCanOpen, ccCanOpenAnywhere } from '../ContactCenter/access';

// The pages the ASC Dashboard carries, and which of them answer for any
// department the person holds rather than for Support alone.
const ASC_VIEWS = ['live_ops', 'agents', 'director', 'manager', 'supervisor', 'team_performance',
  'outbound', 'staffing', 'repeat_callers', 'city', 'email', 'leadership'];
const ANY_DEPARTMENT = new Set(['live_ops', 'agents', 'director', 'manager', 'supervisor', 'team_performance', 'outbound']);

// Which reports this person can open, decided once. The sidebar (AppShell) and
// the landing page (Home) both read this, so the two cannot disagree about who
// reaches what -- a Home card for a page the sidebar hides would be a link that
// only bounces back to Home. The route guards in App.jsx and the server
// (middleware/rbac.js) are the enforcement; this is what gets drawn.
//
// Administration pages (uploads, mappings, user admin) are not here: they are
// tools, not reports, and the landing page does not offer them.
export function moduleAccess({ user, costCenters = [], isAdmin, isFinanceReviewer, hasOvertimeAccess }) {
  const superAdmin = user?.role === 'super_admin';
  return {
    // MOR: admins and finance get the company-wide board; a DVP gets their own
    // cost centers' reviews and never the company board.
    morCompany: !!(isAdmin || isFinanceReviewer),
    morOwn:     !isAdmin && !isFinanceReviewer && costCenters.length > 0,

    // Gated on the per-user cost-center assignment list
    // (user_overtime_cost_centers): true for admin-class roles (bypass) or
    // anyone with at least one assignment.
    overtime: !!hasOvertimeAccess,

    // Fuel kept its two grants: the standalone flag (all cost centers,
    // read-only) or an Overtime assignment (scoped to the user's own sites).
    fuel: !!(hasOvertimeAccess || user?.canViewFuelReport),

    // Inventory and Sales ask the SERVER (routes/auth.js). Inventory used to be
    // `hasOvertimeAccess || canViewInventory`, which was wrong from migration
    // 048 onward: the link appeared for an Overtime assignment that granted no
    // parts, and stayed hidden for an Inventory grant that granted them.
    inventory: !!user?.hasInventoryAccess,
    sales:     !!user?.hasSalesReportAccess,

    // Standalone permissions, independent of role and of cost-center
    // assignment. Time and Wage is named-employee HR data.
    timeWageAudit:   !!(superAdmin || user?.canReviewTimeWageAudit),
    expenseAnalysis: !!(superAdmin || user?.canViewExpenseAnalysis),
    customerBilling: !!(superAdmin || user?.canViewCustomerBilling),
    postCollections: !!(superAdmin || user?.canViewPostCollections),
    softpakReports:  !!(superAdmin || user?.canViewSoftpakReports),
    // Its own all-or-nothing grant (migration 117), independent of Soft-Pak Reports.
    scalePak:        !!(superAdmin || user?.canViewScalePak),
    // Its own all-or-nothing grant (migration 118), apart from both of the above.
    disposal:        !!(superAdmin || user?.canViewDisposal),
    fleetReports:    !!(superAdmin || user?.canViewFleetReports),
    // Cost-center scoped, so the server decides (routes/auth.js), as for Inventory.
    fleetDashboard:  !!user?.hasFleetDashboardAccess,
    // Super admins only while it is compared with Inventory; the API says the same.
    inventoryFleetio: !!superAdmin,
    cityPricing:     !!(superAdmin || user?.canManagePriceSheets || user?.canViewPriceSheets),

    // Drawn when the server would serve this person any ASC page, by the same
    // rule that gates the routes.
    contactCenter: ASC_VIEWS.some((v) => (ANY_DEPARTMENT.has(v) ? ccCanOpenAnywhere(user, v) : ccCanOpen(user, v, 'support'))),

    // Staffing: unrestricted roles (super_admin/admin, NOT finance_reviewer),
    // HR/Recruiting, or DVPs via the Overtime facility assignment. Headcount
    // adds either standalone HR permission.
    staffing:    !!(isAdmin || user?.canViewStaffingTracker || hasOvertimeAccess),
    hrHeadcount: !!(isAdmin || user?.canViewHrHeadcount || user?.canViewHrAllocations || hasOvertimeAccess),
  };
}

// Every report Home can offer, in sidebar order. `key` is also what a person's
// start page is stored as (users.start_page), so these keys must match
// backend/src/services/startPage.js -- a key the server does not know is
// refused there, and a stored key no longer listed here falls back to Home.
export const MODULES = [
  { key: 'mor',             group: 'Operations',           label: 'MOR Dashboard',          to: '/admin/company',    description: "Every yard's month against budget" },
  { key: 'mor_own',         group: 'Operations',           label: 'Monthly Operations Review', to: '/review',        description: "Your yard's month against budget" },
  { key: 'overtime',        group: 'Operations',           label: 'Overtime',               to: '/overtime',         description: 'Overtime hours and cost by yard and role' },
  { key: 'fuel',            group: 'Operations',           label: 'Fuel Report',            to: '/fuel-report',      description: 'Fuel card spend by yard' },
  { key: 'inventory',       group: 'Operations',           label: 'Inventory',              to: '/inventory',        description: 'Parts on hand, aging and below minimum' },
  { key: 'post_collections', group: 'Operations',          label: 'Daily Productivity Report', to: '/post-collections', description: 'Tons in and out, bales, labor and line uptime by site, day by day' },
  { key: 'scale_pak',       group: 'Operations',           label: 'Scale-Pak',              to: '/scale-pak',        description: 'Scale tickets, tons and loads by facility and cost center, live' },
  { key: 'disposal',        group: 'Operations',           label: 'Disposal',               to: '/disposal',         description: 'Where loads were dumped, overweight loads, ticket matching, disposal cost and idle boxes' },
  { key: 'softpak_reports', group: 'Operations',           label: 'Soft-Pak Reports',       to: '/softpak-reports',  description: 'Rebuilt Crystal reports, live from the Soft-Pak replica' },
  { key: 'fleet_dashboard', group: 'Operations',           label: 'R&M Dashboard',          to: '/fleet-dashboard', description: 'Fleet readiness, cost, shop and trucks by cost center' },
  { key: 'fleet_reports',   group: 'Operations',           label: 'Fleet Reports',          to: '/fleet-reports',  description: 'Work orders, purchase orders, uptime and utilization' },
  { key: 'sales',           group: 'Sales & finance',      label: 'Open Market Commercial', to: '/sales-report',     description: 'Revenue and volume by yard and material' },
  { key: 'expense',         group: 'Sales & finance',      label: 'Expense Analysis',       to: '/expense-analysis', description: 'Workday expense lines by category' },
  { key: 'billing',         group: 'Sales & finance',      label: 'Customer Billing',       to: '/customer-billing', description: 'Every customer\'s monthly bill, and what a rate change does to it' },
  { key: 'pricing',         group: 'Sales & finance',      label: 'City Pricing',           to: '/pricing',          description: 'City rate sheets' },
  { key: 'staffing',        group: 'People & compliance',  label: 'Staffing Tracker',       to: '/staffing-tracker', description: 'Staffing levels and mechanic skills' },
  { key: 'hr_headcount',    group: 'People & compliance',  label: 'HR Headcount',           to: '/hr-headcount',     description: 'Headcount and turnover by yard' },
  { key: 'time_wage',       group: 'People & compliance',  label: 'Time and Wage Audit',    to: '/time-wage-audit',  description: 'Meal-break and shift time gaps' },
  { key: 'contact_center',  group: 'Contact center',       label: 'ASC Dashboard',          to: '/contact-center',   description: 'Live ops, agents and service levels' },
];

const GRANT_FOR = {
  mor: 'morCompany', mor_own: 'morOwn', overtime: 'overtime', fuel: 'fuel',
  inventory: 'inventory', post_collections: 'postCollections', softpak_reports: 'softpakReports', scale_pak: 'scalePak', disposal: 'disposal', fleet_reports: 'fleetReports', fleet_dashboard: 'fleetDashboard',
  sales: 'sales', expense: 'expenseAnalysis', billing: 'customerBilling',
  pricing: 'cityPricing', staffing: 'staffing', hr_headcount: 'hrHeadcount',
  time_wage: 'timeWageAudit', contact_center: 'contactCenter',
};

export function reachableModules(access) {
  return MODULES.filter((m) => access[GRANT_FOR[m.key]]);
}

// Where "/" sends this person: their chosen start page when they can still open
// it, otherwise Home. Checked against today's access rather than trusted,
// because a grant can be withdrawn after the choice was made, and a start page
// they can no longer open would bounce straight back here.
export function startPath(user, access) {
  const chosen = reachableModules(access).find((m) => m.key === user?.startPage);
  return chosen ? chosen.to : '/home';
}
